/**
 * Tech-debt #4 — Remote-control / transaction family: UnlockConnector,
 * RemoteStart/StopTransaction, DataTransfer, ClearCache, GetTransactionStatus.
 * These drive the local store's transaction flow.
 */
import { ConnectorId } from '@/domain/types';
import { errorMessage } from '@/lib/errorMessage';
import { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import { useConfigurationStore } from '../useConfigurationStore';
import { useStationStore } from '../useStationStore';
import type { CsmsCtx, CsmsEvent } from './context';

export function handleUnlockConnector(_event: CsmsEvent<'UnlockConnector'>, ctx: CsmsCtx): void {
  // No physical latch to release. v1.6 UnlockConnectorStatus includes
  // `NotSupported`; v2.0.1 UnlockStatusEnumType's closest value is
  // `UnlockFailed`. Reply version-appropriately.
  ctx.dispatcher.reply(ctx.messageId, {
    status: ctx.adapter instanceof OcppV201Adapter ? 'UnlockFailed' : 'NotSupported',
  });
}

export function handleRemoteStartTransaction(
  event: CsmsEvent<'RemoteStartTransaction'>,
  ctx: CsmsCtx,
): void {
  const { adapter, dispatcher, messageId } = ctx;
  // BUG-012 — wire RemoteStart into the local start flow. BUG-021 — honour
  // AuthorizeRemoteTxRequests: when "true", send Authorize.req before
  // StartTransaction (OCPP 1.6J §4.7).
  const state = useStationStore.getState();
  const cid = event.connectorId;
  const connector = cid != null ? state.connectors.get(ConnectorId.of(cid)) : null;
  if (!connector || connector.activeTransactionId !== null) {
    dispatcher.reply(messageId, { status: 'Rejected' });
    return;
  }
  dispatcher.reply(messageId, { status: 'Accepted' });
  const authoriseFirst =
    useConfigurationStore.getState().entries.find((e) => e.key === 'AuthorizeRemoteTxRequests')
      ?.value === 'true';
  const connectorIdBranded = ConnectorId.of(cid as number);
  const idTag = event.idTag;
  if (authoriseFirst) {
    const adapterRef = adapter;
    void (async (): Promise<void> => {
      try {
        const auth = await adapterRef.authorize(idTag).response;
        useStationStore.setState({
          authBanner: { idTag, status: auth.idTagInfo.status, at: Date.now() },
        });
        if (auth.idTagInfo.status === 'Accepted') {
          await state.startTransaction(connectorIdBranded, idTag);
        }
      } catch (err) {
        useStationStore.setState({ lastError: errorMessage(err) });
      }
    })();
  } else {
    void state.startTransaction(connectorIdBranded, idTag);
  }
}

export function handleRemoteStopTransaction(
  event: CsmsEvent<'RemoteStopTransaction'>,
  ctx: CsmsCtx,
): void {
  // BUG-012 — wire RemoteStop into the local stop flow.
  const state = useStationStore.getState();
  const target = [...state.connectors.values()].find(
    (c) =>
      c.activeTransactionId !== null && Number(c.activeTransactionId) === event.transactionId,
  );
  if (!target) {
    ctx.dispatcher.reply(ctx.messageId, { status: 'Rejected' });
    return;
  }
  ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted' });
  void state.stopTransaction(target.id);
}

export function handleDataTransfer(_event: CsmsEvent<'DataTransfer'>, ctx: CsmsCtx): void {
  ctx.dispatcher.reply(ctx.messageId, { status: 'UnknownVendorId' });
}

export function handleClearCache(_event: CsmsEvent<'ClearCache'>, ctx: CsmsCtx): void {
  // BUG-028 / Session 8f.15 — OCPP 1.6J §5.2. Wipe the per-station AuthCache
  // (LocalAuthList rows untouched per spec). Always succeeds locally.
  useStationStore.getState().clearAuthCache();
  ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted' });
}

export function handleGetTransactionStatus(
  event: CsmsEvent<'GetTransactionStatus'>,
  ctx: CsmsCtx,
): void {
  // Session 19a — v2.0.1. `ongoingIndicator`: is the queried tx (or ANY tx
  // when no id given) still running? `messagesInQueue`: we transmit
  // immediately while online, so false.
  const active = [...useStationStore.getState().connectors.values()].filter(
    (c) => c.activeTransactionId !== null,
  );
  const ongoingIndicator =
    event.transactionId !== undefined
      ? active.some((c) => Number(c.activeTransactionId) === event.transactionId)
      : active.length > 0;
  ctx.dispatcher.reply(ctx.messageId, { ongoingIndicator, messagesInQueue: false });
}
